import React, { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import AdminLogo, { useBrandSettings } from "@/components/AdminLogo";
import { Loader2 } from "lucide-react";

export default function AdminLogin() {
  const { t } = useTranslation();
  const { login } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") || "/admin";
  const { data: brand } = useBrandSettings();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const fields = new FormData(e.currentTarget);
      await login(String(fields.get("email") || email).trim(), String(fields.get("password") || password));
      toast.success(t("auth.login_success", "Welcome back"));
      navigate(next, { replace: true });
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : t("auth.login_failed", "Login failed. Check your email and password."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-zinc-950 relative overflow-hidden grain">
      {/* Ambient gold gradient orb */}
      <div
        className="absolute -top-40 -right-40 w-[560px] h-[560px] rounded-full blur-3xl opacity-30 pointer-events-none"
        style={{ background: "radial-gradient(circle, #b8860b 0%, transparent 70%)" }}
      />
      <div
        className="absolute -bottom-40 -left-40 w-[520px] h-[520px] rounded-full blur-3xl opacity-20 pointer-events-none"
        style={{ background: "radial-gradient(circle, #d4a017 0%, transparent 70%)" }}
      />

      <div className="w-full max-w-md p-9 bg-zinc-900/85 backdrop-blur-2xl rounded-[28px] border border-zinc-800/80 shadow-2xl relative">
        <div className="flex items-center gap-3 mb-8">
          <AdminLogo size={48} variant="light" />
          <div className="leading-tight">
            <div className="font-serif text-xl text-white tracking-tight">
              {brand?.company_name || "Adelina"}
            </div>
            <div className="text-[10px] tracking-[0.24em] uppercase text-zinc-500 mt-0.5 font-medium">
              {t("brand.tagline", "Employment Agency")}
            </div>
          </div>
        </div>
        <div className="text-[11px] tracking-[0.22em] uppercase text-[#d4a017] font-semibold mb-2" data-testid="admin-overline">
          {t("auth.admin_access", "Admin Access")}
        </div>
        <h1 className="font-serif text-white text-3xl mb-8 tracking-tight">
          {t("auth.signin_title", "Sign in to Adelina CMS")}
        </h1>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="text-zinc-300 text-xs uppercase tracking-wider font-medium">{t("auth.email", "Email")}</Label>
            <Input type="email" name="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5 bg-zinc-800/70 border-zinc-700 text-white focus:border-[#d4a017]" data-testid="admin-email" required />
          </div>
          <div>
            <Label className="text-zinc-300 text-xs uppercase tracking-wider font-medium">{t("auth.password", "Password")}</Label>
            <Input type="password" name="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1.5 bg-zinc-800/70 border-zinc-700 text-white focus:border-[#d4a017]" data-testid="admin-password" required />
          </div>
          <button type="submit" disabled={loading} className="btn-gold w-full disabled:opacity-60 flex items-center justify-center gap-2 mt-2" data-testid="admin-submit">
            {loading && <Loader2 size={16} className="animate-spin" />}
            {loading ? t("auth.signing_in", "Signing in…") : t("auth.signin_btn", "Sign In")}
          </button>
          <div className="text-xs text-zinc-500 text-center pt-2">
            <Link to="/forgot-password" className="hover:text-[#d4a017] transition-colors" data-testid="forgot-pw-link">
              {t("auth.forgot_link", "Forgot password?")}
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
